import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  Mail,
  Eye,
  RefreshCw,
  Calendar,
  User,
  Package,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  getAbandonedCartsThunk,
  markAbandonedCartsThunk,
} from "../../../store/thunks/adminCartThunks";
import LoadingSpinner from "../../../components/common/Loader";
import { setCartFilters } from "store/slices/adminCartSlice";

const AbandonedCarts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { abandonedCarts, abandonedPagination, loadingAnalytics, error } =
    useSelector((state) => state.cart);

  const [daysFilter, setDaysFilter] = useState(7);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markDays, setMarkDays] = useState(7);

  useEffect(() => {
    fetchAbandonedCarts();
  }, [daysFilter, abandonedPagination.currentPage]);

  const fetchAbandonedCarts = () => {
    dispatch(
      getAbandonedCartsThunk({
        daysOld: daysFilter,
        page: abandonedPagination.currentPage,
        limit: abandonedPagination.pageSize,
      })
    );
  };

  const handleMarkAbandoned = async () => {
    await dispatch(markAbandonedCartsThunk(markDays)).unwrap();
    setShowMarkModal(false);
    fetchAbandonedCarts();
  };

  const handleViewCart = (cartId) => {
    navigate(`/cart/details/${cartId}`);
  };

  const handlePageChange = (page) => {
    dispatch(setCartFilters({ page }));
  };

  const getDaysColor = (days) => {
    if (days <= 3) return "text-green-600 bg-green-100";
    if (days <= 7) return "text-yellow-600 bg-yellow-100";
    if (days <= 14) return "text-orange-600 bg-orange-100";
    return "text-red-600 bg-red-100";
  };

  if (loadingAnalytics && abandonedCarts.length === 0)
    return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abandoned Carts</h1>
          <p className="text-gray-600 mt-1">
            Recover abandoned carts and boost conversions
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowMarkModal(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Mark as Abandoned
          </button>
          <button
            onClick={fetchAbandonedCarts}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">
            Show carts inactive for:
          </label>
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={3}>3+ days</option>
            <option value={7}>7+ days</option>
            <option value={14}>14+ days</option>
            <option value={30}>30+ days</option>
            <option value={60}>60+ days</option>
            <option value={90}>90+ days</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      {/* Abandoned Carts List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {abandonedCarts.length === 0 ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No abandoned carts found</p>
            <p className="text-sm text-gray-400 mt-2">
              Try adjusting the filter to show more results
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cart Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recovery Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {abandonedCarts.map((cart) => (
                  <tr key={cart.cartId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-full">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {cart.user?.name || "Unknown"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {cart.user?.email}
                          </p>
                          {cart.user?.phone && (
                            <p className="text-xs text-gray-500">
                              {cart.user.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium">
                            {cart.itemCount} items
                          </span>
                        </div>
                        {cart.exchangeItemCount > 0 && (
                          <span className="text-sm text-gray-600">
                            + {cart.exchangeItemCount} exchange
                          </span>
                        )}
                      </div>
                      {cart.appliedCoupons?.length > 0 && (
                        <div className="mt-1">
                          {cart.appliedCoupons.map((coupon, index) => (
                            <span
                              key={index}
                              className="inline-flex px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded mr-1"
                            >
                              {coupon}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-900">
                            {new Date(cart.lastActivity).toLocaleDateString()}
                          </p>
                          <span
                            className={`inline-flex px-2 py-1 text-xs rounded-full mt-1 ${getDaysColor(
                              cart.daysSinceActivity
                            )}`}
                          >
                            {cart.daysSinceActivity} days ago
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500">
                          No recovery attempts
                        </span>
                        <button className="text-xs text-blue-600 hover:text-blue-800 text-left">
                          Send recovery email
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewCart(cart.cartId)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Cart"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          className="text-green-600 hover:text-green-800"
                          title="Send Recovery Email"
                        >
                          <Mail className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {abandonedPagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing{" "}
              {(abandonedPagination.currentPage - 1) *
                abandonedPagination.pageSize +
                1}{" "}
              to{" "}
              {Math.min(
                abandonedPagination.currentPage * abandonedPagination.pageSize,
                abandonedPagination.totalCount
              )}{" "}
              of {abandonedPagination.totalCount} carts
            </div>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  handlePageChange(abandonedPagination.currentPage - 1)
                }
                disabled={abandonedPagination.currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() =>
                  handlePageChange(abandonedPagination.currentPage + 1)
                }
                disabled={
                  abandonedPagination.currentPage ===
                  abandonedPagination.totalPages
                }
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recovery Tips */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3">Recovery Tips</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              Send recovery emails within 24-48 hours for best results
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              Offer incentives like discounts or free shipping to encourage
              completion
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Use personalized messaging based on cart contents</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Follow up with SMS for high-value carts</span>
          </li>
        </ul>
      </div>

      {/* Mark Abandoned Modal */}
      {showMarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Mark Carts as Abandoned
            </h3>
            <p className="text-gray-600 mb-4">
              Mark all active carts that have been inactive for a specified
              number of days as abandoned.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Days of Inactivity
              </label>
              <select
                value={markDays}
                onChange={(e) => setMarkDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This will mark all active carts inactive for {markDays}+ days as
                abandoned
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowMarkModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAbandoned}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Mark as Abandoned
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbandonedCarts;
